import { ArrowUpRight, ArrowDown, Hammer, Briefcase, Store, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Aurora glows */}
      <div
        aria-hidden
        className="aurora-glow w-[500px] h-[500px] top-10 -right-20 hidden md:block"
        style={{
          background:
            "radial-gradient(circle, rgba(168,85,247,0.45), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="aurora-glow w-[400px] h-[400px] bottom-10 left-1/3 hidden md:block"
        style={{
          background:
            "radial-gradient(circle, rgba(236,72,153,0.30), transparent 70%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
        {/* Eyebrow row */}
        <div className="flex items-center justify-between mb-10 md:mb-12 fade-up">
          <span className="eyebrow">Donnez à votre activité la visibilité qu'elle mérite.</span>
          <span className="hidden md:inline-flex items-center gap-2 text-xs text-[#0F172A]/70">
            <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7] animate-pulse" />
            Sur mesure · pas de template
          </span>
        </div>

        {/* Magazine split */}
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-10 items-center">
          {/* LEFT: Editorial type */}
          <div className="col-span-12 lg:col-span-6 fade-up delay-1">
            <h1
              data-testid="hero-headline"
              className="font-display text-[clamp(2.5rem,5.8vw,5rem)] leading-[1.02] tracking-tight text-[#0B1027] font-medium"
            >
              Votre savoir-faire mérite d'être{" "}
              <em className="text-aurora not-italic font-display italic font-light">
                vu, compris et choisi.
              </em>
            </h1>

            <p className="mt-8 md:mt-10 text-base md:text-lg text-[#0F172A]/75 leading-relaxed max-w-xl">
              J'accompagne artisans, indépendants, petites entreprises
              et associations dans la création complète de leur identité
              numérique. Un seul interlocuteur, du premier croquis à la mise
              en ligne, avec la même exigence que vous mettez dans votre métier.
            </p>

            {/* Audience strip — Option A : icônes inline minimalistes */}
            <div
              data-testid="hero-audience-strip"
              className="mt-7 md:mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-[13px] md:text-sm text-[#0F172A]/75"
            >
              <span className="inline-flex items-center gap-2">
                <Hammer size={16} strokeWidth={1.7} className="text-[#A855F7]" />
                Artisans
              </span>
              <span aria-hidden className="hidden md:inline-block h-[3px] w-[3px] rounded-full bg-[#0F172A]/30" />
              <span className="inline-flex items-center gap-2">
                <Briefcase size={16} strokeWidth={1.7} className="text-[#A855F7]" />
                Indépendants
              </span>
              <span aria-hidden className="hidden md:inline-block h-[3px] w-[3px] rounded-full bg-[#0F172A]/30" />
              <span className="inline-flex items-center gap-2">
                <Store size={16} strokeWidth={1.7} className="text-[#A855F7]" />
                Petites entreprises
              </span>
              <span aria-hidden className="hidden md:inline-block h-[3px] w-[3px] rounded-full bg-[#0F172A]/30" />
              <span className="inline-flex items-center gap-2">
                <Users size={16} strokeWidth={1.7} className="text-[#A855F7]" />
                Associations
              </span>
            </div>

            <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                data-testid="hero-booking-cta"
                className="rounded-full btn-aurora h-14 px-8 text-sm tracking-wide font-semibold shadow-tactile hover-lift"
              >
                <a href="#reservation" className="inline-flex items-center gap-2">
                  Réserver un appel
                  <ArrowUpRight size={18} />
                </a>
              </Button>
              <a
                href="#realisations"
                data-testid="hero-secondary-cta"
                className="btn-aurora-border inline-flex items-center justify-center gap-2 rounded-full h-14 px-8 text-sm tracking-wide font-semibold whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-2">
                  Voir les réalisations
                  <ArrowDown size={16} />
                </span>
              </a>
            </div>
          </div>

          {/* RIGHT: Device composition */}
          <div className="col-span-12 lg:col-span-6 relative fade-up delay-2">
            <div className="relative h-[460px] md:h-[560px] lg:h-[600px]">
              {/* MacBook with Chango's */}
              <div
                data-testid="hero-macbook"
                className="macbook absolute top-4 left-0 right-12 lg:right-16 shadow-device"
              >
                <div className="macbook-screen">
                  <img
                    src="/changos-screenshot.png"
                    alt="Chango's Renovation — site réalisé"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="macbook-base" />
                {/* Project label */}
                <div
                  data-testid="hero-macbook-label"
                  className="absolute -bottom-3 left-6 md:left-10 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-tactile border border-[#0B1027]/8 flex items-center gap-2"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#A855F7]" />
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#0B1027]/70 font-bold">
                    Réalisation
                  </span>
                  <span className="text-[10px] text-[#0B1027]/40">·</span>
                  <span className="text-[11px] text-[#0B1027] font-medium">
                    Chango's
                  </span>
                </div>
              </div>

              {/* iPhone with Teralya */}
              <div
                data-testid="hero-iphone"
                className="iphone absolute bottom-0 right-0 w-[140px] md:w-[160px] rotate-[8deg]"
              >
                <div className="iphone-notch" />
                <div className="iphone-screen">
                  <img
                    src="/teralya-screenshot.png"
                    alt="Teralya — site réalisé"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Project label */}
                <div
                  data-testid="hero-iphone-label"
                  className="absolute -bottom-2 -left-4 bg-white/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-tactile border border-[#0B1027]/8 flex items-center gap-1.5 rotate-[-12deg]"
                >
                  <span className="h-1 w-1 rounded-full bg-[#EC4899]" />
                  <span className="text-[9px] uppercase tracking-[0.16em] text-[#0B1027]/70 font-bold">
                    Réa.
                  </span>
                  <span className="text-[10px] text-[#0B1027] font-medium">
                    Teralya
                  </span>
                </div>
              </div>

              {/* Floating badge — 100% sur-mesure */}
              <div
                className="float-badge absolute bottom-20 left-0 rotate-[3deg] text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
                  color: "white",
                }}
              >
                <div>
                  <div className="font-display text-2xl text-white leading-none">
                    100 %
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/85 font-bold mt-1">
                    Sur-mesure
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
